import React, { useState, useEffect } from 'react';
import { Tour, Modal, Button, Card, Avatar } from 'antd';
import {
  HomeOutlined, ProjectOutlined, ShoppingOutlined, UserOutlined,
  WalletOutlined, SafetyCertificateOutlined, RocketOutlined,
  CrownOutlined, MessageOutlined, BellOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const t = {
  ar: {
    welcomeTitle: 'مرحباً بك في لانسر سيتي! 🎉',
    welcomeFreelancer: 'لنقم بجولة قصيرة لنريك كيف تربح من خلال تقديم خدماتك للعملاء.',
    welcomeClient: 'لنقم بجولة قصيرة لنريك كيف توظف أفضل المستقلين العرب.',
    start: 'لنبدأ', skip: 'تخطي', finish: 'إنهاء', next: 'التالي', prev: 'السابق',

    // Freelancer steps
    fStep1Title: 'أكمل ملفك الشخصي', fStep1Desc: 'ملف احترافي يزيد فرصك بنسبة 5 أضعاف. أضف صورة، نبذة، مهارات، وسعر الساعة.',
    fStep2Title: 'تحقق من هويتك (KYC)', fStep2Desc: 'يفتح لك السحب البنكي ويزيد ثقة العملاء بك. الوثائق مشفرة.',
    fStep3Title: 'أضف خدماتك', fStep3Desc: 'انشر خدماتك بأسعار ثابتة (3 مستويات: أساسي/متوسط/متقدم) ليطلبها العملاء مباشرة.',
    fStep4Title: 'قدّم عروضاً على المشاريع', fStep4Desc: 'تصفح المشاريع المفتوحة وقدّم عروضاً مخصصة. لديك 10 عروض شهرياً مجاناً.',
    fStep5Title: 'تابع رصيدك', fStep5Desc: 'اطّلع على أرباحك، اسحبها عبر PayPal أو التحويل البنكي عند بلوغ الحد الأدنى.',

    // Client steps
    cStep1Title: 'أكمل ملفك', cStep1Desc: 'ملف عميل موثوق يجذب أفضل المستقلين.',
    cStep2Title: 'انشر مشروعك', cStep2Desc: 'صف ما تحتاجه بوضوح: المهام، الميزانية، والمدة. كلما كان الوصف مفصلاً، حصلت على عروض أفضل.',
    cStep3Title: 'تصفح المستقلين', cStep3Desc: 'اطّلع على ملفاتهم، تقييماتهم، ومعارض أعمالهم قبل الاختيار.',
    cStep4Title: 'شحن الرصيد', cStep4Desc: 'يجب شحن المبلغ في الرصيد لقبول عرض. المبلغ يبقى في الضمان حتى تقبل التسليم.',
    cStep5Title: 'تواصل مع المستقلين', cStep5Desc: 'استخدم نظام المراسلة المدمج للنقاش والاتفاق على التفاصيل.',
  },
  en: {
    welcomeTitle: 'Welcome to LancerCity! 🎉',
    welcomeFreelancer: "Let's take a quick tour to show you how to start earning by offering your services.",
    welcomeClient: "Let's take a quick tour to show you how to hire the best Arab freelancers.",
    start: "Let's go", skip: 'Skip', finish: 'Finish', next: 'Next', prev: 'Back',

    fStep1Title: 'Complete your profile', fStep1Desc: 'A pro profile gets 5x more proposals. Add photo, bio, skills, and hourly rate.',
    fStep2Title: 'Verify your identity (KYC)', fStep2Desc: 'Unlocks bank withdrawals and boosts client trust. Documents are encrypted.',
    fStep3Title: 'Add your services', fStep3Desc: 'Publish gigs at fixed prices (3 tiers: basic/standard/premium) so clients can order directly.',
    fStep4Title: 'Submit proposals to projects', fStep4Desc: 'Browse open projects and send custom proposals. 10 free proposals/month.',
    fStep5Title: 'Track your wallet', fStep5Desc: 'See your earnings, withdraw via PayPal or bank wire once you hit the minimum.',

    cStep1Title: 'Complete your profile', cStep1Desc: 'A complete client profile attracts top freelancers.',
    cStep2Title: 'Post your project', cStep2Desc: 'Describe what you need clearly: tasks, budget, duration. The more detail, the better the proposals.',
    cStep3Title: 'Browse freelancers', cStep3Desc: 'Review profiles, ratings, and portfolios before choosing.',
    cStep4Title: 'Top up your balance', cStep4Desc: 'You must fund your wallet to accept a proposal. The amount stays in escrow until you approve delivery.',
    cStep5Title: 'Chat with freelancers', cStep5Desc: 'Use the built-in messaging to discuss and agree on details.',
  },
};

const OnboardingTour = ({ lang }) => {
  const tx = t[lang] || t.ar;
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  useEffect(() => {
    if (loading || !user) return;
    const key = `onboarding_done_${user.id}`;
    const done = localStorage.getItem(key);
    if (!done) {
      // Show welcome dialog after a brief delay so the page can render first.
      // CRITICAL: mark as shown IMMEDIATELY so it never appears again, even if the user
      // closes the modal with the X, refreshes, or navigates away mid-tour.
      const timer = setTimeout(() => {
        setShowWelcome(true);
        try { localStorage.setItem(key, '1'); } catch (e) {}
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [user, loading]);

  const dismiss = () => {
    if (user) localStorage.setItem(`onboarding_done_${user.id}`, '1');
    setShowWelcome(false);
    setTourOpen(false);
  };

  const startTour = () => {
    setShowWelcome(false);
    setTimeout(() => { setTourOpen(true); setTourStep(0); }, 200);
  };

  if (!user) return null;
  const isFreelancer = user.role === 'freelancer';

  // Build tour steps
  const freelancerSteps = [
    {
      title: <><UserOutlined /> {tx.fStep1Title}</>,
      description: tx.fStep1Desc,
      target: () => document.querySelector('a[href="/profile"]') || document.body,
      nextButtonProps: { children: tx.next },
      prevButtonProps: { children: tx.prev },
    },
    {
      title: <><SafetyCertificateOutlined /> {tx.fStep2Title}</>,
      description: tx.fStep2Desc,
      target: () => document.body,
    },
    {
      title: <><ShoppingOutlined /> {tx.fStep3Title}</>,
      description: tx.fStep3Desc,
      target: () => document.querySelector('a[href="/my-gigs"]') || document.body,
    },
    {
      title: <><ProjectOutlined /> {tx.fStep4Title}</>,
      description: tx.fStep4Desc,
      target: () => document.querySelector('a[href="/projects"]') || document.body,
    },
    {
      title: <><WalletOutlined /> {tx.fStep5Title}</>,
      description: tx.fStep5Desc,
      target: () => document.querySelector('a[href="/wallet"]') || document.body,
      nextButtonProps: { onClick: dismiss, children: tx.finish },
    },
  ];

  const clientSteps = [
    {
      title: <><UserOutlined /> {tx.cStep1Title}</>,
      description: tx.cStep1Desc,
      target: () => document.querySelector('a[href="/profile"]') || document.body,
    },
    {
      title: <><ProjectOutlined /> {tx.cStep2Title}</>,
      description: tx.cStep2Desc,
      target: () => document.querySelector('a[href="/projects/create"]') || document.body,
    },
    {
      title: <><CrownOutlined /> {tx.cStep3Title}</>,
      description: tx.cStep3Desc,
      target: () => document.querySelector('a[href="/freelancers"]') || document.body,
    },
    {
      title: <><WalletOutlined /> {tx.cStep4Title}</>,
      description: tx.cStep4Desc,
      target: () => document.querySelector('a[href="/wallet"]') || document.body,
    },
    {
      title: <><MessageOutlined /> {tx.cStep5Title}</>,
      description: tx.cStep5Desc,
      target: () => document.querySelector('a[href="/chat"]') || document.body,
      nextButtonProps: { onClick: dismiss, children: tx.finish },
    },
  ];

  const steps = isFreelancer ? freelancerSteps : clientSteps;

  return (
    <>
      <Modal open={showWelcome} onCancel={dismiss} centered closable={false}
        footer={[
          <Button key="skip" onClick={dismiss}>{tx.skip}</Button>,
          <Button key="start" type="primary" size="large" icon={<RocketOutlined />}
            onClick={startTour}
            style={{ paddingInline: 30, height: 42, borderRadius: 10, fontWeight: 700 }}>
            {tx.start}
          </Button>,
        ]}
        title={null} width={520}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary)', marginBottom: 12 }}>
            {tx.welcomeTitle}
          </h2>
          <p style={{ color: '#5D6D7E', fontSize: 15, lineHeight: 1.8 }}>
            {isFreelancer ? tx.welcomeFreelancer : tx.welcomeClient}
          </p>
        </div>
      </Modal>

      <Tour open={tourOpen} onClose={dismiss} steps={steps}
        current={tourStep} onChange={setTourStep}
        type="primary" />
    </>
  );
};

export default OnboardingTour;
